import { useState, useEffect } from 'react';
import { BookOpen, FileText, Calendar, Users, TrendingUp, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import api from '../../services/api';

const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#8b5cf6'];

function StatCard({ title, value, icon: Icon, color = 'indigo' }) {
  const colorClasses = {
    indigo: 'from-indigo-500 to-indigo-600',
    purple: 'from-purple-500 to-purple-600',
    pink: 'from-pink-500 to-pink-600',
    green: 'from-green-500 to-green-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-3xl font-bold text-gray-800 mt-2">{value}</p>
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-br text-white ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

export default function LecturerDashboard() {
  const [dashboard, setDashboard] = useState({ assigned_subjects: 0, total_exams: 0, total_attendance_sessions: 0, subjects: [] });
  const [analytics, setAnalytics] = useState({ subjectExams: [], attendanceStats: [] });
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [currentSubjectIndex, setCurrentSubjectIndex] = useState(0);

  useEffect(() => { 
    loadDashboard();
    loadStudentPerformance();
  }, []);

  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const len = dashboard.subjects?.length || 0;
    if (len <= 3) {
      setCurrentSubjectIndex(0);
      return;
    }
    setCurrentSubjectIndex(0);
    const timer = setInterval(() => {
      if (!isPaused) setCurrentSubjectIndex(s => (s + 1) % len);
    }, 3500);
    return () => clearInterval(timer);
  }, [dashboard.subjects?.length, isPaused]);

  const loadDashboard = async () => {
    try {
      const res = await api.get('/lecturer/dashboard');
      
      if (res.data.success && res.data.data && res.data.data.dashboard) {
        const dashboardData = res.data.data.dashboard;
        
        setDashboard(dashboardData);
        
        // Generate analytics from subjects data
        const subjectExams = dashboardData.subjects.map(subject => ({
          name: subject.subject_name,
          grade: subject.grade_name,
          grade_id: subject.grade_id,
          exams: subject.total_exams || 0
        }));

        // Use attendance_by_grade data from backend
        const attendanceStats = dashboardData.attendance_by_grade || [];
        
        setAnalytics({
          subjectExams,
          attendanceStats
        });
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const loadStudentPerformance = async () => {
    try {
      const res = await api.get('/lecturer/student-performance');
      if (res.data.success && res.data.data) {
        setPerformance(res.data.data.performance || []);
      }
    } catch (err) {
    }
  };


  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  // If dashboard data is missing or API failed, show a friendly error
  if (!dashboard || typeof dashboard !== 'object' || !('assigned_subjects' in dashboard)) {
    return <div className="flex items-center justify-center h-64 text-red-500 font-semibold">Failed to load dashboard data. Please try again later.</div>;
  }

  // Extract unique grades for filter
  const grades = ['all', ...new Set(dashboard.subjects.map(s => s.grade_name))];
  
  // Filter analytics data based on selected grade and period
  const filteredExams = selectedGrade === 'all' 
    ? analytics.subjectExams 
    : analytics.subjectExams.filter(item => item.grade === selectedGrade);
    
  const filteredAttendance = selectedGrade === 'all'
    ? analytics.attendanceStats
    : analytics.attendanceStats.filter(item => item.grade_name === selectedGrade);



  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Lecturer Dashboard</h1>
        <p className="text-gray-500">Manage your exams and attendance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard title="Assigned Subjects" value={dashboard.assigned_subjects} icon={BookOpen} color="indigo" />
        <StatCard title="Total Exams" value={dashboard.total_exams} icon={FileText} color="purple" />
        <StatCard title="Attendance Sessions" value={dashboard.total_attendance_sessions} icon={Calendar} color="pink" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Exams by Subject Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-semibold text-gray-800">Exams by Subject</h3>
            </div>
            <div className="flex gap-2">
              <select 
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                {grades.map(grade => (
                  <option key={grade} value={grade}>
                    {grade === 'all' ? 'All Grades' : grade}
                  </option>
                ))}
              </select>
              <select 
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Time</option>
                <option value="1m">Last Month</option>
                <option value="3m">Last 3 Months</option>
                <option value="6m">Last 6 Months</option>
                <option value="1y">Last Year</option>
              </select>
            </div>
          </div>
          {filteredExams.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={filteredExams}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#9ca3af" padding={{ left: 20, right: 20, top: 100 }} height={80} />
                <YAxis stroke="#9ca3af" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  cursor={{ fill: 'rgba(99, 102, 241, 0.1)' }}
                />
                <Bar dataKey="exams" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-400 text-center py-12">No exam data available</p>
          )}
        </div>

        {/* Attendance Sessions Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-pink-600" />
              <h3 className="text-lg font-semibold text-gray-800">Attendance Distribution</h3>
            </div>
            <div className="flex gap-2">
              <select 
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              >
                {grades.map(grade => (
                  <option key={grade} value={grade}>
                    {grade === 'all' ? 'All Grades' : grade}
                  </option>
                ))}
              </select>
              <select 
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              >
                <option value="all">All Time</option>
                <option value="1m">Last Month</option>
                <option value="3m">Last 3 Months</option>
                <option value="6m">Last 6 Months</option>
                <option value="1y">Last Year</option>
              </select>
            </div>
          </div>
          {filteredAttendance.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={filteredAttendance}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="grade_name" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  cursor={{ fill: 'rgba(236, 72, 153, 0.1)' }}
                  formatter={(value) => `${value} sessions`}
                />
                <Bar dataKey="sessions" fill="#ec4899" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-400 text-center py-12">No attendance data</p>
          )}
        </div>
      </div>

      {/* Top Performers by Grade */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-800">Top Performing Students by Grade</h3>
        </div>
        {performance.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {performance.map((gradeData, idx) => (
              <div key={idx} className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100">
                <p className="text-sm font-semibold text-green-800 mb-3">{gradeData.grade_name}</p>
                <div className="space-y-2">
                  {gradeData.top_performers && gradeData.top_performers.length > 0 ? (
                    gradeData.top_performers.map((student, sidx) => (
                      <div key={sidx} className="flex items-center justify-between">
                        <p className="text-sm text-gray-700">{student.name}</p>
                        <span className="px-2 py-1 bg-green-200 text-green-800 text-xs font-semibold rounded-full">{student.avg}%</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-500 text-center py-2">No top performers yet</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No student data available</p>
        )}
      </div>

      {/* Students Needing Improvement by Grade */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-orange-600" />
          <h3 className="text-lg font-semibold text-gray-800">Students Needing Improvement by Grade</h3>
        </div>
        {performance.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {performance.map((gradeData, idx) => (
              <div key={idx} className="p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-100">
                <p className="text-sm font-semibold text-orange-800 mb-3">{gradeData.grade_name}</p>
                <div className="space-y-2">
                  {gradeData.needs_improvement && gradeData.needs_improvement.length > 0 ? (
                    gradeData.needs_improvement.map((student, sidx) => (
                      <div key={sidx} className="flex items-center justify-between">
                        <p className="text-sm text-gray-700">{student.name}</p>
                        <span className="px-2 py-1 bg-orange-200 text-orange-800 text-xs font-semibold rounded-full">{student.avg}%</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-500 text-center py-2">All students performing well!</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-8">No student data available</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Assigned Subjects */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Assigned Subjects</h3>
          {dashboard.subjects.length > 0 ? (
            <div>
              <div className="relative">
                <div className="space-y-3" style={{ minHeight: '220px' }}>
                  {(() => {
                    const len = dashboard.subjects.length;
                    const visible = len <= 3 ? dashboard.subjects : [0,1,2].map(i => dashboard.subjects[(currentSubjectIndex + i) % len]);
                    return visible.map((subject, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-indigo-50 transition">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg" style={{ backgroundColor: `${COLORS[(currentSubjectIndex + idx) % COLORS.length]}20` }}>
                            <BookOpen className="w-5 h-5" style={{ color: COLORS[(currentSubjectIndex + idx) % COLORS.length] }} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{subject.subject_name}</p>
                            <p className="text-sm text-gray-500">{subject.grade_name}</p>
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>

                {dashboard.subjects.length > 3 && (
                  <div className="absolute right-2 top-2 flex gap-2">
                    <button onClick={() => setCurrentSubjectIndex((s) => (s - 1 + dashboard.subjects.length) % dashboard.subjects.length)} className="p-2 bg-white rounded-full shadow-sm text-gray-600 hover:bg-gray-50">◀</button>
                    <button onClick={() => setCurrentSubjectIndex((s) => (s + 1) % dashboard.subjects.length)} className="p-2 bg-white rounded-full shadow-sm text-gray-600 hover:bg-gray-50">▶</button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-gray-400 text-center py-8">No subjects assigned yet</p>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-4">
            <a href="/lecturer/exams" className="p-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl text-white hover:shadow-lg transition">
              <FileText className="w-6 h-6 mb-2" />
              <p className="font-medium text-sm">Create Exam</p>
              <p className="text-xs text-purple-100">Add new exam</p>
            </a>
            <a href="/lecturer/attendance" className="p-4 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl text-white hover:shadow-lg transition">
              <Calendar className="w-6 h-6 mb-2" />
              <p className="font-medium text-sm">Mark Attendance</p>
              <p className="text-xs text-indigo-100">Record session</p>
            </a>
            <a href="/lecturer/exams" className="p-4 bg-gradient-to-r from-pink-500 to-orange-500 rounded-xl text-white hover:shadow-lg transition col-span-2">
              <Users className="w-6 h-6 mb-2" />
              <p className="font-medium text-sm">Enter Marks</p>
              <p className="text-xs text-pink-100">Enter student marks for exams</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
